import { Outlet, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useUser } from "../../contexts/UserContext";
import Button from 'react-bootstrap/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import CourseSelector from '../../components/CourseSelector';
import { useCourse } from "../../contexts/CourseContext";
import { useTopics } from "../../contexts/TopicsContext";
import { useEffect, useRef } from "react";
import { analyticsService } from '../../services/api';


export default function Root() {
    
    const { roleData, logout, user } = useUser(); // Access roleData and logout function from context
    const { resetTopicsContext } = useTopics();
    const navigate = useNavigate();
    const location = useLocation();
    const { courses, openJoinModal, resetCourseContext, coursesInitialized, showJoinModal, selectedCourse } = useCourse();
    const prevCoursesInitialized = useRef(false);

    // Honor an explicit post-login redirect (e.g., /lab-buddy) once user is available
    useEffect(() => {
        if (!user) return;
        try {
            const redirect = sessionStorage.getItem('postLoginRedirect') || localStorage.getItem('postLoginRedirect');
            if (redirect && location.pathname !== redirect) {
                if (redirect === '/lab-buddy' && user.chatbot_access !== true) {
                    sessionStorage.removeItem('postLoginRedirect');
                    localStorage.removeItem('postLoginRedirect');
                    console.info('[nav] skipping lab-buddy redirect (no chatbot access)');
                    navigate('/dashboard');
                    return;
                }
                sessionStorage.removeItem('postLoginRedirect');
                localStorage.removeItem('postLoginRedirect');
                sessionStorage.setItem('postLoginRedirectUsed', 'true');
                localStorage.setItem('postLoginRedirectUsed', 'true');
                console.info('[nav] honoring postLoginRedirect', redirect);
                navigate(redirect);
                return; // avoid triggering other redirects in same tick
            }
        } catch (e) {
            console.warn('[nav] redirect check failed', e);
        }
    }, [user, navigate, location.pathname]);

    
    useEffect(() => {
        // only act after we know whether courses were loaded
        if (!user) {
            prevCoursesInitialized.current = false;
            return;
        }
        // If we have a redirect hint/usage or are on lab buddy/consent, do not override with dashboard redirect
        try {
            const usedRedirect = sessionStorage.getItem('postLoginRedirectUsed') === 'true' || localStorage.getItem('postLoginRedirectUsed') === 'true';
            const redirectHint = sessionStorage.getItem('postLoginRedirect') || localStorage.getItem('postLoginRedirect');
            const onChatbot = location.pathname.startsWith('/lab-buddy');
            const onConsent = location.pathname.startsWith('/consent');
            if (usedRedirect || redirectHint || onChatbot || onConsent) {
                console.info('[nav] skipping dashboard redirect (redirect guard)', { usedRedirect, redirectHint, path: location.pathname });
                return;
            }
        } catch (e) {
            console.warn('[nav] usedRedirect check failed', e);
        }
        if (!coursesInitialized) {
            prevCoursesInitialized.current = false;
            return;
        }  // still fetching — wait
        // if user has no courses, force them to dashboard and open modal
        // Only act on the transition from "not initialized" -> "initialized"
        if (!prevCoursesInitialized.current && coursesInitialized) {
        prevCoursesInitialized.current = true;

                // If there are no courses, only auto-open the join modal when the user just logged in
                if (Array.isArray(courses) && courses.length === 0) {
                        if (location.pathname !== "/dashboard") {
                            navigate("/dashboard");
                        }
                        try {
                            const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
                            if (justLoggedIn && !showJoinModal) {
                                openJoinModal();
                                // clear the transient flag so this only happens once
                                sessionStorage.removeItem('justLoggedIn');
                            }
                        } catch (err) {
                            // If sessionStorage unavailable, default to opening the modal
                            if (!showJoinModal) openJoinModal();
                        }
                }
        }
    }, [
        user,
        coursesInitialized,
        // include courses.length so effect re-runs when list is returned, but we still gate by the transition ref
        Array.isArray(courses) ? courses.length : null,
        navigate,
        openJoinModal,
        location.pathname,
        showJoinModal,
    ]);

    console.log("Courses in Root:", courses);
    console.log("Role data:", roleData);
    console.log("User data:", user);
    const handleLogout = () => {
        logout(resetCourseContext, resetTopicsContext); // Clear all context and storage
        navigate('/'); // Navigate to the home page after logout
    };
    
    return (
        <>  
    
            <div id="header-container">
                <div id="heading">
                    <div id="site-title">
                        <div className="site-title-multiple">
                            
                            <h1 className="site-name site-name-large">BrainBoost Instructional System</h1>
                                
                        </div>
                    </div>
                </div>
                <div className="button-group">

                    {/* Unified course selector for all roles - only shows if user has multiple courses */}
                    {roleData && <CourseSelector />}

                    {/* Role-specific dashboard buttons */}
                    {(roleData === 'instructor') ? (
                        <>
                            <Link to="/instructor">
                                <button className="header-button">Instructor Dashboard</button>
                            </Link>
                            <Link to="/dashboard">
                                <button className="header-button">Student Dashboard</button>
                            </Link>
                        </>
                    ) : (roleData === 'student') ? (
                        <>
                            <Link to="/dashboard" onClick={() => analyticsService.logEvent(user?.studentID, null, 'student_dashboard_nav', 'from_header')}>
                                <button className="header-button">Student Dashboard</button>
                            </Link>
                        </>
                    ) : null}

                    {/* Lab Buddy — only for GTIDs allowed on the backend (see chatbot_access on /me) */}
                    {roleData && user?.chatbot_access === true && selectedCourse?.name?.includes('1301') && (
                        <Link to="/lab-buddy">
                            <button className="header-button">Lab Buddy</button>
                        </Link>
                    )}

                    {/* Conditionally render the Logout button */}
                    {roleData && (
                        <button
                            onClick={handleLogout}
                            className="header-button"
                        >
                            Logout
                        </button>
                    )}
                </div>
            </div>

            <AnimatePresence mode="wait">
                <Outlet key={location.pathname} />
            </AnimatePresence>
        </>
    );
}

